/**
 * ChipPopover — Mini contextual popover anchored above a config chip.
 *
 * Renders via createPortal to document.body with fixed positioning.
 * Shows all options for a chip parameter with the active one highlighted.
 * Closes on click-outside or Escape.
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

export interface ChipOption {
  value: string;
  label: string;
}

interface ChipPopoverProps {
  options: ChipOption[];
  activeValue: string;
  anchorRect: DOMRect;
  onSelect: (value: string) => void;
  onClose: () => void;
}

const ChipPopover: React.FC<ChipPopoverProps> = ({
  options,
  activeValue,
  anchorRect,
  onSelect,
  onClose,
}) => {
  const { theme } = useTheme();
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on click-outside
  // Uses 'click' (not 'mousedown') so chip buttons can stopPropagation() to prevent
  // the close handler from firing when the user clicks a chip to cycle values.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // setTimeout(0) avoids the same click that opened the popover from closing it
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Position: centered above the chip anchor
  // Use estimated width for initial positioning; auto-width via CSS
  const estimatedWidth = 150;
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const left = Math.max(
    8,
    Math.min(anchorCenterX - estimatedWidth / 2, window.innerWidth - estimatedWidth - 8)
  );
  const bottom = window.innerHeight - anchorRect.top + 6;

  return createPortal(
    <div
      ref={popoverRef}
      role="listbox"
      className={`fixed z-[9999] rounded-xl border ${theme.colors.border.primary} ${theme.colors.bg.primary} shadow-xl py-1 animate-in fade-in slide-in-from-bottom-1 duration-150`}
      style={{
        left: `${left}px`,
        bottom: `${bottom}px`,
        minWidth: '120px',
        width: 'max-content',
        maxWidth: '220px',
      }}
    >
      {options.map((opt) => {
        const isActive = opt.value === activeValue;
        return (
          <button
            key={opt.value}
            type="button"
            role="option"
            aria-selected={isActive}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(opt.value);
            }}
            className={`flex items-center gap-1.5 w-full px-2.5 py-1.5 text-[12px] text-left rounded-md transition-colors ${
              isActive
                ? 'text-blue-400'
                : `${theme.colors.text.secondary} hover:${theme.colors.text.primary}`
            } hover:${theme.colors.bg.secondary}`}
          >
            <span className="flex-1">{opt.label}</span>
            {isActive && <Check className="w-3 h-3 text-blue-400 flex-shrink-0" />}
          </button>
        );
      })}
    </div>,
    document.body
  );
};

export default React.memo(ChipPopover);
